import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout';
import {
  approveAgentStep,
  buildAgentEventsUrl,
  createAgentPlan,
  getAgentHealth,
  getAgentRunReplay,
  getAgentRunState,
  pauseAgentRun,
  resumeAgentRun,
  startAgentRun,
  stopAgentRun,
} from '../services/agentApi';
import { FiPlay, FiStopCircle, FiCheckCircle, FiXCircle, FiActivity } from 'react-icons/fi';

const TASK_TEMPLATES = [
  { key: 'custom', label: 'Custom Prompt', prompt: '' },
  { key: 'dashboard-summary', label: 'Dashboard KPI Summary', prompt: 'Open operations dashboard and summarize pending requests.' },
  { key: 'requests-pending', label: 'Service Requests: Pending Count', prompt: 'Open requests page and count pending tickets.' },
  { key: 'requests-recent', label: 'Service Requests: Recent List', prompt: 'Open requests page and list latest service requests.' },
  { key: 'chat-sessions', label: 'Live Chat: Active Sessions', prompt: 'Open chat page and list active sessions.' },
  { key: 'guests-overview', label: 'Guests Overview', prompt: 'Open guests page and summarize current guests.' },
  { key: 'rooms-overview', label: 'Rooms Overview', prompt: 'Open rooms page and summarize occupancy availability.' },
  { key: 'bookings-summary', label: 'Activity and Bookings Summary', prompt: 'Open bookings page and summarize recent entries.' },
  { key: 'slots-overview', label: 'Slot Management Overview', prompt: 'Open slots page and summarize utilization.' },
];

function AgentPilot() {
  const [prompt, setPrompt] = useState('Open operations dashboard and summarize pending requests.');
  const [taskTemplate, setTaskTemplate] = useState('dashboard-summary');
  const [tenantId, setTenantId] = useState('elychat-default');
  const [health, setHealth] = useState(null);
  const [plan, setPlan] = useState(null);
  const [runId, setRunId] = useState('');
  const [events, setEvents] = useState([]);
  const [latestScreenshot, setLatestScreenshot] = useState('');
  const [currentStep, setCurrentStep] = useState(null);
  const [awaitingApprovalStep, setAwaitingApprovalStep] = useState(null);
  const [latestExtract, setLatestExtract] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [runState, setRunState] = useState(null);
  const [runSummary, setRunSummary] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (!runId) return undefined;

    let cancelled = false;
    const refresh = async () => {
      try {
        const data = await getAgentRunState(runId);
        if (cancelled) return;
        setRunState(data?.run || null);
        setRunSummary(data?.summary || null);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError('Run state not found on server (service restart or expired in-memory run). Create a new run.');
          setRunState((prev) => ({ ...(prev || {}), status: 'stopped' }));
          return;
        }
        // Keep UI non-blocking for polling failures.
      }
    };

    refresh();
    const timer = setInterval(refresh, 2000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [runId]);

  useEffect(() => {
    loadHealth();
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const loadHealth = async () => {
    try {
      const data = await getAgentHealth();
      setHealth(data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load Agent bridge health');
    }
  };

  const handleTemplateChange = (nextTemplate) => {
    setTaskTemplate(nextTemplate);
    const selected = TASK_TEMPLATES.find((item) => item.key === nextTemplate);
    if (selected?.prompt) {
      setPrompt(selected.prompt);
    }
  };

  const handleCreatePlan = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await createAgentPlan(prompt, tenantId);
      setPlan(data.plan);
      setRunId('');
      setEvents([]);
      setLatestScreenshot('');
      setCurrentStep(null);
      setAwaitingApprovalStep(null);
      setLatestExtract(null);
      setCursor(null);
      setRunState(null);
      setRunSummary(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  const openEventStream = async (nextRunId) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = await buildAgentEventsUrl(nextRunId);
    const source = new EventSource(url);
    eventSourceRef.current = source;

    source.onmessage = (message) => {
      try {
        const parsed = JSON.parse(message.data);
        setEvents((prev) => [...prev, parsed]);

        if (parsed?.type === 'agent.run.screenshot.updated' && parsed?.payload?.dataUrl) {
          setLatestScreenshot(parsed.payload.dataUrl);
        }

        if (parsed?.type === 'agent.run.step.started') {
          setCurrentStep(parsed.payload || null);
        }

        if (parsed?.type === 'agent.run.awaiting_approval') {
          setAwaitingApprovalStep(parsed.payload || null);
        }

        if (parsed?.type === 'agent.run.step.completed') {
          setAwaitingApprovalStep(null);
        }

        if (parsed?.type === 'agent.run.extract.result') {
          setLatestExtract(parsed?.payload?.extracted || null);
        }

        if (parsed?.type === 'agent.run.cursor.moved') {
          const payload = parsed?.payload || {};
          if (typeof payload.x === 'number' && typeof payload.y === 'number') {
            setCursor({
              x: payload.x,
              y: payload.y,
              viewportWidth: typeof payload.viewportWidth === 'number' ? payload.viewportWidth : 1280,
              viewportHeight: typeof payload.viewportHeight === 'number' ? payload.viewportHeight : 720,
              reason: payload.reason || 'move',
            });
          }
        }
      } catch {
        // Ignore malformed messages.
      }
    };

    source.onerror = () => {
      // Keep existing events; user can restart run or refresh stream.
    };
  };

  const handleStartRun = async () => {
    if (!plan?.id) return;

    setLoading(true);
    setError('');

    try {
      const data = await startAgentRun(plan.id);
      setRunId(data.runId);
      setRunState({ status: data.status, runId: data.runId });
      await openEventStream(data.runId);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to start run');
    } finally {
      setLoading(false);
    }
  };

  const handleStopRun = async () => {
    if (!runId) return;

    setLoading(true);
    setError('');

    try {
      await stopAgentRun(runId);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to stop run');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseRun = async () => {
    if (!runId) return;
    setLoading(true);
    setError('');

    try {
      await pauseAgentRun(runId);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to pause run');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeRun = async () => {
    if (!runId) return;
    setLoading(true);
    setError('');

    try {
      await resumeAgentRun(runId);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to resume run');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approved) => {
    if (!runId) return;
    setLoading(true);
    setError('');

    try {
      const targetStepIndex =
        Number.isInteger(awaitingApprovalStep?.stepIndex)
          ? awaitingApprovalStep.stepIndex
          : Number.isInteger(currentStep?.stepIndex)
          ? currentStep.stepIndex
          : 0;

      await approveAgentStep(runId, targetStepIndex, approved);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to send approval');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadReplay = async () => {
    if (!runId) return;
    setReplayLoading(true);
    setError('');

    try {
      const data = await getAgentRunReplay(runId);
      const replay = data?.replay;
      const replayEvents = replay?.events || [];

      setEvents(replayEvents);
      setRunState(replay?.run || null);
      setRunSummary(replay?.summary || null);

      const latestStepEvent = [...replayEvents]
        .reverse()
        .find((item) => item?.type === 'agent.run.step.started' && item?.payload);
      setCurrentStep(latestStepEvent?.payload || null);

      const latestAwaitingEvent = [...replayEvents]
        .reverse()
        .find((item) => item?.type === 'agent.run.awaiting_approval' && item?.payload);
      setAwaitingApprovalStep(latestAwaitingEvent?.payload || null);

      const screenshotEvent = [...replayEvents]
        .reverse()
        .find((item) => item?.type === 'agent.run.screenshot.updated' && item?.payload?.dataUrl);

      if (screenshotEvent?.payload?.dataUrl) {
        setLatestScreenshot(screenshotEvent.payload.dataUrl);
      }

      const cursorEvent = [...replayEvents]
        .reverse()
        .find((item) => item?.type === 'agent.run.cursor.moved' && typeof item?.payload?.x === 'number');

      const extractEvent = [...replayEvents]
        .reverse()
        .find((item) => item?.type === 'agent.run.extract.result' && item?.payload?.extracted);

      setLatestExtract(extractEvent?.payload?.extracted || null);

      if (cursorEvent?.payload) {
        setCursor({
          x: cursorEvent.payload.x,
          y: cursorEvent.payload.y,
          viewportWidth: cursorEvent.payload.viewportWidth || 1280,
          viewportHeight: cursorEvent.payload.viewportHeight || 720,
          reason: cursorEvent.payload.reason || 'move',
        });
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load replay');
    } finally {
      setReplayLoading(false);
    }
  };

  const latestEventLabel = useMemo(() => {
    const last = events[events.length - 1];
    return last?.type || 'No live events yet';
  }, [events]);

  const terminalStatuses = ['completed', 'failed', 'stopped'];
  const isTerminalRun = terminalStatuses.includes(String(runState?.status || '').toLowerCase());
  const isPausedRun = String(runState?.status || '').toLowerCase() === 'paused';

  const durationSec = runSummary?.durationMs
    ? Math.max(0, Math.round(runSummary.durationMs / 1000))
    : 0;

  const cursorLeftPercent = cursor
    ? Math.max(0, Math.min(100, (cursor.x / Math.max(1, cursor.viewportWidth)) * 100))
    : 0;
  const cursorTopPercent = cursor
    ? Math.max(0, Math.min(100, (cursor.y / Math.max(1, cursor.viewportHeight)) * 100))
    : 0;

  return (
    <Layout>
      <div className="space-y-6 px-1 py-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiActivity className="text-gray-700" />
            Agent Pilot
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Elychat bridge to Agent Fabric. Safe pilot mode for admin/manager only.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Create Plan</h2>

            <label className="block text-sm font-medium text-gray-700">Tenant ID</label>
            <input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />

            <label className="block text-sm font-medium text-gray-700">Task Template</label>
            <select
              value={taskTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
            >
              {TASK_TEMPLATES.map((template) => (
                <option key={template.key} value={template.key}>{template.label}</option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700">Task Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                setTaskTemplate('custom');
              }}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleCreatePlan}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <FiPlay /> Create Plan
              </button>
              <button
                onClick={handleStartRun}
                disabled={loading || !plan?.id || (!!runId && !isTerminalRun)}
                className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <FiPlay /> Start Run
              </button>
              <button
                onClick={handlePauseRun}
                disabled={loading || !runId || isTerminalRun || isPausedRun}
                className="inline-flex items-center gap-2 bg-yellow-500 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                Pause Run
              </button>
              <button
                onClick={handleResumeRun}
                disabled={loading || !runId || isTerminalRun || !isPausedRun}
                className="inline-flex items-center gap-2 bg-indigo-600 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                Resume Run
              </button>
              <button
                onClick={handleStopRun}
                disabled={loading || !runId || isTerminalRun}
                className="inline-flex items-center gap-2 bg-red-600 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <FiStopCircle /> Stop Run
              </button>
              <button
                onClick={handleLoadReplay}
                disabled={loading || replayLoading || !runId}
                className="inline-flex items-center gap-2 bg-slate-700 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {replayLoading ? 'Loading...' : 'Load Replay'}
              </button>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleApprove(true)}
                disabled={loading || !runId || isTerminalRun || !awaitingApprovalStep}
                className="inline-flex items-center gap-2 bg-green-600 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <FiCheckCircle /> Approve {Number.isInteger(awaitingApprovalStep?.stepIndex) ? `Step ${awaitingApprovalStep.stepIndex}` : 'Step'}
              </button>
              <button
                onClick={() => handleApprove(false)}
                disabled={loading || !runId || isTerminalRun || !awaitingApprovalStep}
                className="inline-flex items-center gap-2 bg-orange-500 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <FiXCircle /> Reject {Number.isInteger(awaitingApprovalStep?.stepIndex) ? `Step ${awaitingApprovalStep.stepIndex}` : 'Step'}
              </button>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Run & Health</h2>
            <div className="text-sm text-gray-700">
              <p><strong>Bridge health:</strong> {health?.ok ? 'Connected' : 'Unknown'}</p>
              <p><strong>Plan ID:</strong> {plan?.id || '-'}</p>
              <p><strong>Run ID:</strong> {runId || '-'}</p>
              <p><strong>Run status:</strong> {runState?.status || '-'}</p>
              <p><strong>Failure reason:</strong> {runSummary?.failureReason || '-'}</p>
              <p><strong>Latest event:</strong> {latestEventLabel}</p>
              <p><strong>Current step:</strong> {currentStep?.description || '-'}</p>
              <p><strong>Awaiting approval:</strong> {awaitingApprovalStep?.description || 'No'}</p>
              <p><strong>Cursor:</strong> {cursor ? `${Math.round(cursor.x)}, ${Math.round(cursor.y)} (${cursor.reason})` : '-'}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs text-gray-700">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3"><strong>Duration:</strong> {durationSec}s</div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3"><strong>Approvals:</strong> {runSummary?.approvalsCount ?? 0}</div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3"><strong>Steps:</strong> {runSummary?.stepsCompleted ?? 0}/{runSummary?.stepsStarted ?? 0}</div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3"><strong>Screenshots:</strong> {runSummary?.screenshotsEmitted ?? 0}</div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs text-gray-700 mb-2"><strong>Latest extract result</strong></p>
              <pre className="text-[11px] leading-5 text-gray-700 overflow-x-auto whitespace-pre-wrap break-words">
                {latestExtract ? JSON.stringify(latestExtract, null, 2) : 'No extract result yet.'}
              </pre>
            </div>

            <div className="border border-gray-200 rounded-lg p-3 bg-black/95 min-h-[200px] flex items-center justify-center overflow-hidden">
              {latestScreenshot ? (
                <div className="flex items-center justify-center w-full">
                  <div className="relative inline-block">
                    <img
                      src={latestScreenshot}
                      alt="Agent visual execution"
                      className="max-h-64 w-auto rounded border border-gray-700"
                    />
                    {cursor ? (
                      <div
                        className="absolute pointer-events-none"
                        style={{ left: `${cursorLeftPercent}%`, top: `${cursorTopPercent}%`, transform: 'translate(-50%, -50%)' }}
                      >
                        <div className="h-4 w-4 rounded-full bg-cyan-400 border-2 border-white shadow-[0_0_10px_rgba(34,211,238,0.9)]" />
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-300">No screenshot yet. Start run to see visual execution.</p>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-3 h-72 overflow-y-auto bg-gray-50">
              {events.length === 0 ? (
                <p className="text-sm text-gray-500">No events yet.</p>
              ) : (
                <div className="space-y-2">
                  {events.map((event, idx) => (
                    <div key={`${event.type || 'event'}-${idx}`} className="text-xs text-gray-700">
                      <strong>{event.type || 'event'}</strong> {event.timestamp ? `- ${new Date(event.timestamp).toLocaleTimeString()}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AgentPilot;
